import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getLaboklinApi } from '../../services/laboklinApi';
import type { LabReport } from '../../types/laboklin';
import { LABOKLIN_BORDER, LABOKLIN_RED } from '../../theme/laboklinTheme';
import ResultsTable from './ResultsTable';
import InterpretationPanel from './InterpretationPanel';
import DifferentialsPanel from './DifferentialsPanel';
import NextTestsPanel from './NextTestsPanel';
import { useLabWorkup } from './useLabWorkup';

/**
 * A report and its workup, side by side.
 *
 * The report is Laboklin's, unchanged. The column beside it is the same answer the
 * chat drawer gives, laid out as panels: what the values mean, where the
 * differential stands, what was set aside, and the tests that would settle what is
 * still open. The veterinarian does not ask for it - opening the report starts it.
 */

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <>
    <Box sx={{ color: 'text.secondary', fontSize: 14 }}>{label}</Box>
    <Box sx={{ fontSize: 14 }}>{value}</Box>
  </>
);

export const ReportWorkupView: React.FC = () => {
  const { reportNo } = useParams<{ reportNo: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<LabReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { workup, phase, progress } = useLabWorkup(reportNo);

  const sectionRefs = useRef<Map<string, HTMLElement | null>>(new Map());
  const [hoveredAnalytes, setHoveredAnalytes] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (!reportNo) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    getLaboklinApi()
      .getReport(reportNo)
      .then((data) => {
        if (!cancelled) setReport(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reportNo]);

  /** Analyte id -> the interpretation line that reasons from it. */
  const linkedSections = useMemo(() => {
    const map = new Map<string, string>();
    for (const section of workup?.sections ?? []) {
      for (const analyteId of section.analyteIds) {
        if (!map.has(analyteId)) map.set(analyteId, section.title);
      }
    }
    return map;
  }, [workup]);

  const scrollToSection = (analyteId: string) => {
    const title = linkedSections.get(analyteId);
    if (!title) return;
    sectionRefs.current.get(title)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (loading) {
    return (
      <Paper sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (error || !report) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ?? 'Report not found.'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/mylab2')}>
          Back
        </Button>
      </Box>
    );
  }

  const sampleMaterials = [...new Set(report.results.map((r) => r.material).filter(Boolean))];
  const patient = [report.patient.breed, report.patient.species, report.patient.sex]
    .filter(Boolean)
    .join(', ');

  return (
    <Box>
      <Box
        component="button"
        onClick={() => navigate('/mylab2')}
        sx={{
          border: 0,
          bgcolor: 'transparent',
          color: 'text.secondary',
          fontSize: 13,
          mb: 1.25,
          p: 0,
          cursor: 'pointer',
        }}
      >
        ‹ All reports
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 520px' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        <Paper sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2.75,
              py: 2,
              borderBottom: `1px solid ${LABOKLIN_BORDER}`,
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              Report {report.reportNo}{' '}
              <Box component="span" sx={{ color: '#2e7d32', fontWeight: 400, ml: 1 }}>
                {report.reportType ?? 'Final'}
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', gap: 2.25, color: LABOKLIN_RED, fontSize: 14 }}>
              {/* Laboklin's own actions, inert here as they are in the drawer view. */}
              <span>Download as PDF</span>
              <span>Download as LDT</span>
              <span>Share report</span>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '150px 1fr',
              rowGap: 1,
              px: 2.75,
              py: 2.25,
            }}
          >
            <DetailRow label="Received" value={formatDate(report.receiveDate)} />
            <DetailRow label="Reported" value={formatDate(report.reportDate)} />
            {patient && <DetailRow label="Patient" value={patient} />}
            {report.clinicalSigns && (
              <DetailRow label="Clinical signs" value={report.clinicalSigns} />
            )}
            {sampleMaterials.length > 0 && (
              // Laboklin's own material codes, untranslated. They are often German and
              // the sample changes what a value means, so a tidier label here would be
              // a second, wronger copy of something the report already states.
              <DetailRow label="Sample" value={sampleMaterials.join(', ')} />
            )}
          </Box>

          <Box sx={{ px: 2.75, pb: 2.75, pt: 1, borderTop: `1px solid ${LABOKLIN_BORDER}` }}>
            <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 1.25, mt: 1.5 }}>
              Test results
              {report.abnormalCount > 0 && (
                <Typography component="span" sx={{ color: 'text.secondary', ml: 1, fontSize: 13 }}>
                  · {report.abnormalCount} outside the reference range
                </Typography>
              )}
            </Typography>
            <ResultsTable
              results={report.results}
              linkedSections={linkedSections}
              onTagClick={scrollToSection}
              {...(hoveredAnalytes ? { highlightedAnalyteIds: hoveredAnalytes } : {})}
            />
          </Box>
        </Paper>

        {/* Bounded to the viewport and scrolled internally. Sticky alone only pins
            the top of the column, so a workup longer than the screen could be read
            just by scrolling the page - and the report beside it, which is the
            longer of the two, moved at the same time. The two columns are read
            against each other, so each has to move on its own. */}
        <Paper
          sx={{
            position: { lg: 'sticky' },
            top: 80,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { lg: 'calc(100vh - 96px)' },
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              borderBottom: `1px solid ${LABOKLIN_BORDER}`,
              display: 'flex',
              alignItems: 'baseline',
              gap: 1.25,
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 16 }}>Diagnostic support</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>powered by GekkoVet</Typography>
          </Box>

          {/* The scroller. Bounding the height without this silently CLIPS: a full
              workup runs to about 1800px against a 700px column, so the differential
              and every suggested test below it were cut off with no scrollbar to say
              so - and an empty-looking panel reads as a pipeline that never finished
              rather than as content out of view. minHeight:0 is what lets a flex
              child shrink below its content and actually scroll. */}
          <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
            {phase === 'error' && (
              <Box sx={{ p: 2.5 }}>
                <Alert severity="error">The workup could not be started.</Alert>
              </Box>
            )}

            {!workup && phase !== 'error' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 2.5 }}>
                <CircularProgress size={14} thickness={5} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {progress ?? 'Reading the laboratory data…'}
                </Typography>
              </Box>
            )}

            {workup && (
              <>
                <InterpretationPanel
                  workup={workup}
                  sectionRefs={sectionRefs}
                  onSectionHover={(ids) => setHoveredAnalytes(ids ? new Set(ids) : null)}
                />
                <DifferentialsPanel
                  workup={workup}
                  waiting={phase !== 'ready'}
                  progress={progress}
                />
                <NextTestsPanel
                  tests={workup.tests}
                  onConfirm={() => navigate('/mylab2/order/new')}
                />
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ReportWorkupView;
